import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import Gamburger from './Gamburger/Gamburger';
import OptionsPopUp from './OptionsPopUp/OptionsPopUp';
import EmptyListPlaceHolder from './EmptyListPlaceHolder/EmptyListPlaceHolder';

import { hexToRGB, SetImage } from 'common/utils/helpers';
import { POLYGON_OPTIONS_MODAL_MODE } from 'common/utils/constants';
import { setActivePolygon } from 'redux/actions';
import { polygonFillOpacity, OPTIONS_POP_UP_POSITION_X_CORRECTION } from '../constants';
import {
  getCoordinatesFromReference,
  reorderList
} from '../functions';
import './PolygonContentSection.css';

const PolygonContentSection = React.forwardRef((props, ref) => {
  const {
    polygonList = [],
    listStyle,
    handlerShowPolygonOptionsModal,
    allowMapPopUpActivityAction,
    toggleDeleteConfirmationModal,
    exportPolygon,
    setPolygonsSorting,
    setMapActivatedPolygonMode
  } = props;

  const activePolygonId = useSelector((store) => store.ui.devicesPage.activePolygonId);
  const dispatch = useDispatch();

  const { currentListHeightRef } = ref.current;
  const [currentHoveredPolygon, setCurrentHoveredPolygon] = useState(null);
  const [currentHoveredPolygonIcon, setCurrentHoveredPolygonIcon] = useState(null);
  const [currentOptionsPopUpInfo, setCurrentOptionsPopUpInfo] = useState({
    isShow: false,
    left: 0,
    top: 0,
    currentPolygon: null
  });
  const [currentDeviceGroupId, setCurrentDeviceGroupId] = useState(null);
  const [isGamburgerActive, setIsGamburgerActive] = useState(true);

  // Polygon events
  const handlerPolygonDelete = () => {
    toggleDeleteConfirmationModal(true);
  };

  const polygonActivationEvent = deviceGroupId => {
    dispatch(setActivePolygon(deviceGroupId === activePolygonId ? null : deviceGroupId));
  };

  // OptionsPopUp shows and calculates
  const handlerClickOnGamburger = (gamburgerRef, currentPolygon) => {
    setIsGamburgerActive(false);
    setMapActivatedPolygonMode('gamburger-showing');

    const top = getCoordinatesFromReference(gamburgerRef).y - getCoordinatesFromReference(currentListHeightRef).y;
    setCurrentOptionsPopUpInfo((prev) => ({
      ...prev,
      isShow: true,
      left: OPTIONS_POP_UP_POSITION_X_CORRECTION + 'px',
      top: top + 'px',
      currentPolygon
    }));
    setCurrentDeviceGroupId(currentPolygon.deviceGroupId);

    allowMapPopUpActivityAction(false);
  };

  // OptionsPopUp closes and hides
  const optionsPopUpClose = (optionsPopUpCurrentItem) => {
    setIsGamburgerActive(true);
    setCurrentOptionsPopUpInfo((prev) => ({
      ...prev,
      isShow: false
    }));
    setCurrentDeviceGroupId(null);

    allowMapPopUpActivityAction(true);
    if (optionsPopUpCurrentItem === 'edit') {
      handlerShowPolygonOptionsModal(currentOptionsPopUpInfo.currentPolygon, POLYGON_OPTIONS_MODAL_MODE.UPDATION);
    }

    if (optionsPopUpCurrentItem === 'duplicate') {
      handlerShowPolygonOptionsModal(currentOptionsPopUpInfo.currentPolygon, POLYGON_OPTIONS_MODAL_MODE.DUPLICATION);
    }

    if (optionsPopUpCurrentItem === 'export') {
      exportPolygon(currentOptionsPopUpInfo.currentPolygon.deviceGroupId);
    }

    if (optionsPopUpCurrentItem === 'delete') {
      handlerPolygonDelete();
    }
  };

  const handlerDragEnd = (result) => {
    const targetPolygon = polygonList.find((polygon) => polygon.deviceGroupId === result.draggableId);
    const isDefaultPolygonExist = polygonList.findIndex((polygon) => polygon.properties.default) !== -1;

    if (!result.destination || (targetPolygon.properties.default || (isDefaultPolygonExist && result.destination.index === 0))) {
      return;
    }

    const items = reorderList(polygonList, result.source.index, result.destination.index);

    setPolygonsSorting({ polygonIds: items.map(polygon => polygon.deviceGroupId) });
  };

  return (
    <div className="PolygonContentSection">
      <div className="PolygonContentSection_List">
        <DragDropContext onDragEnd={handlerDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div
                className='PolygonContentSection_List_Items scroll-component'
                ref={currentListHeightRef}
                style={listStyle}
              >
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {polygonList.map((polygonItem, index) => {
                    const isHovered = currentHoveredPolygon === polygonItem.deviceGroupId;
                    const isActive = polygonItem.deviceGroupId === activePolygonId;
                    const name = polygonItem.properties.name;
                    const isDefault = polygonItem.properties.default;
                    let style = {};
                    if (isActive) {
                      style = {
                        backgroundColor: hexToRGB(polygonItem.properties.fill, polygonFillOpacity['active']),
                        paddingRight: isDefault ? '80px' : '30px'
                      };
                    } else if (isHovered) {
                      style = {
                        backgroundColor: hexToRGB(polygonItem.properties.fill, polygonFillOpacity['hover']),
                        paddingRight: isDefault ? '80px' : '30px'
                      };
                    } else {
                      style = {
                        backgroundColor: '#ffffff',
                        paddingRight: isDefault ? '80px' : '30px'
                      };
                    }

                    return (
                      <Draggable
                        key={polygonItem.deviceGroupId}
                        draggableId={polygonItem.deviceGroupId}
                        index={index}
                      >
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <div
                              className="PolygonContentSection_List_Item"
                              key={polygonItem.deviceGroupId}
                              style={style}
                              onMouseEnter={() => setCurrentHoveredPolygon(polygonItem.deviceGroupId)}
                              onMouseLeave={() => setCurrentHoveredPolygon(null)}
                              data-tip={name}
                            >
                              <div
                                className='PolygonContentSection_List_Item_Listener'
                                onClick={() => polygonActivationEvent(polygonItem.deviceGroupId)}
                              />
                              <div
                                className={classNames({
                                  PolygonContentSection_List_Item_Icon: true,
                                  PolygonContentSection_List_Item_Icon_Hovered: currentHoveredPolygonIcon === polygonItem.deviceGroupId
                                })}
                                onMouseEnter={() => setCurrentHoveredPolygonIcon(polygonItem.deviceGroupId)}
                                onMouseLeave={() => setCurrentHoveredPolygonIcon(null)}
                                onClick={() => polygonActivationEvent(polygonItem.deviceGroupId)}
                              >
                                {SetImage(
                                  polygonItem.properties.stroke,
                                  polygonItem.properties.fill,
                                  polygonItem.properties.fillOpacity
                                )}
                              </div>

                              <div className="PolygonContentSection_List_Item_Name">
                                {name}
                              </div>

                              {isDefault && <div className="MapControlPopUp_Item_Default">Default</div>}

                              <div
                                className="PolygonContentSection_List_Item_Gamburger_Wrapper"
                                onClick={props.stopPolygonEditingShape}
                              >
                                <Gamburger
                                  currentPolygon={polygonItem}
                                  isSelected={currentDeviceGroupId === polygonItem.deviceGroupId}
                                  isActive={isGamburgerActive}
                                  handlerGamburgerClick={handlerClickOnGamburger}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {polygonList.length === 0 && <EmptyListPlaceHolder />}

      <div className="PolygonContentSection_Background" />

      {currentOptionsPopUpInfo.isShow && (
        <OptionsPopUp
          left={currentOptionsPopUpInfo.left}
          top={currentOptionsPopUpInfo.top}
          poligonListRef={currentListHeightRef}
          onClick={optionsPopUpClose}
        />
      )}
    </div>
  );
});

export default PolygonContentSection;
